import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, LanguageContext, API } from '../shared';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Edit, Minus, Plus, Trash2 } from 'lucide-react';


const AgencyManagement = () => {
  const { t } = useContext(LanguageContext);
  const { user } = useContext(AuthContext);
  const [agencies, setAgencies] = useState([]);
  const [agencyBalances, setAgencyBalances] = useState({}); // Store balances for all agencies
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingAgency, setEditingAgency] = useState(null);

  // 🆕 Settlement (مخالصة) dialog state — replaces the unreliable
  // window.confirm + window.prompt chain that Chrome silently aborts.
  const [settlementTarget, setSettlementTarget] = useState(null); // {id, name} or null
  const [settlementConfirmText, setSettlementConfirmText] = useState('');
  const [settlementSubmitting, setSettlementSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    phone_numbers: [''],
    fax_number: '',
    commercial_register_number: '',
    tax_registration_number: '',
    address: ''
  });

  // Only Super Admin can access this
  useEffect(() => {
    if (user?.role !== 'super_admin') {
      return;
    }
    fetchAgencies();
  }, [user]);

  const fetchAgencies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agencies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgencies(response.data);
      
      // Fetch balances for all agencies (Super Admin only)
      if (user?.role === 'super_admin') {
        const balances = {};
        for (const agency of response.data) {
          try {
            const balanceRes = await axios.get(`${API}/agencies/${agency.id}/balance`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            balances[agency.id] = balanceRes.data.current_balance || 0;
          } catch (error) {
            console.error(`Error fetching balance for agency ${agency.name}:`, error);
            balances[agency.id] = 0;
          }
        }
        setAgencyBalances(balances);
      }
    } catch (error) {
      console.error('Error fetching agencies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const submitData = {
        ...formData,
        phone_numbers: formData.phone_numbers.filter(phone => phone.trim() !== '')
      };

      if (editingAgency) {
        await axios.put(`${API}/agencies/${editingAgency.id}`, submitData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        alert('✅ تم تحديث الوكالة بنجاح');
      } else {
        await axios.post(`${API}/agencies`, submitData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        alert('✅ تم إنشاء الوكالة بنجاح');
      }
      
      setShowAddDialog(false);
      setEditingAgency(null);
      setFormData({
        name: '',
        city: '',
        phone_numbers: [''],
        fax_number: '',
        commercial_register_number: '',
        tax_registration_number: '',
        address: ''
      });
      fetchAgencies();
    } catch (error) {
      console.error('Error saving agency:', error);
      alert('خطأ في حفظ الوكالة: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (agency) => {
    setEditingAgency(agency);
    setFormData({
      name: agency.name,
      city: agency.city,
      phone_numbers: agency.phone_numbers || [''],
      fax_number: agency.fax_number || '',
      commercial_register_number: agency.commercial_register_number || '',
      tax_registration_number: agency.tax_registration_number || '',
      address: agency.address || ''
    });
    setShowAddDialog(true);
  };

  const handleDelete = async (agencyId, agencyName) => {
    if (!window.confirm(`هل أنت متأكد من حذف وكالة "${agencyName}"؟\n\nتحذير: سيتم حذف جميع البيانات المرتبطة بهذه الوكالة (المستخدمين، العملاء، العمليات، إلخ)`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/agencies/${agencyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('✅ تم حذف الوكالة بنجاح');
      fetchAgencies();
    } catch (error) {
      console.error('Error deleting agency:', error);
      if (error.response?.status === 400) {
        alert('❌ لا يمكن حذف هذه الوكالة لأنها مرتبطة ببيانات موجودة');
      } else if (error.response?.status === 403) {
        alert('❌ ليس لديك صلاحية لحذف الوكالات');
      } else {
        alert('❌ فشل في حذف الوكالة: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  // Open the settlement dialog (replaces the chained native popups that
  // were silently aborted by Chrome). The actual API call lives in
  // `confirmSettlement` below and only runs after the user types "تأكيد".
  const handleClearAllTransactions = (agencyId, agencyName) => {
    setSettlementTarget({ id: agencyId, name: agencyName });
    setSettlementConfirmText('');
  };

  const closeSettlementDialog = () => {
    if (settlementSubmitting) return;
    setSettlementTarget(null);
    setSettlementConfirmText('');
  };

  const confirmSettlement = async () => {
    if (!settlementTarget || settlementConfirmText.trim() !== 'تأكيد') return;
    setSettlementSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/agencies/${settlementTarget.id}/clear-all-transactions`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('✅ تم تمام المخالصة بنجاح - تم إعادة تعيين جميع حسابات الوكالة إلى صفر');
      setSettlementTarget(null);
      setSettlementConfirmText('');
      fetchAgencies();
    } catch (error) {
      console.error('Error clearing transactions:', error);
      if (error.response?.status === 403) {
        alert('❌ ليس لديك صلاحية لتمام المخالصة');
      } else {
        alert('❌ فشل في تمام المخالصة: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setSettlementSubmitting(false);
    }
  };

  const addPhoneNumber = () => {
    setFormData({
      ...formData,
      phone_numbers: [...formData.phone_numbers, '']
    });
  };

  const updatePhoneNumber = (index, value) => {
    const updatedPhones = [...formData.phone_numbers];
    updatedPhones[index] = value;
    setFormData({
      ...formData,
      phone_numbers: updatedPhones
    });
  };

  const removePhoneNumber = (index) => {
    if (formData.phone_numbers.length > 1) {
      const updatedPhones = formData.phone_numbers.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        phone_numbers: updatedPhones
      });
    }
  };

  if (user?.role !== 'super_admin') {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-bold text-red-600 mb-4">⛔ وصول محظور</h2>
        <p className="text-gray-600">هذا القسم مخصص للمدير العام فقط</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">🏢 إدارة الوكالات</h1>
        <Button onClick={() => setShowAddDialog(true)} className="bg-brand-600 hover:bg-brand-700">
          <Plus className="h-4 w-4 ml-2" />
          إضافة وكالة جديدة
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>قائمة الوكالات</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">اسم الوكالة</TableHead>
                  <TableHead className="text-right">المدينة</TableHead>
                  <TableHead className="text-right">أرقام الهاتف</TableHead>
                  <TableHead className="text-right">السجل التجاري</TableHead>
                  <TableHead className="text-right">الرقم الضريبي</TableHead>
                  <TableHead className="text-right">الرصيد الحالي</TableHead>
                  <TableHead className="text-right">الإجراءات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {agencies.map((agency) => (
                  <TableRow key={agency.id}>
                    <TableCell className="font-medium text-right">{agency.name}</TableCell>
                    <TableCell className="text-right">{agency.city}</TableCell>
                    <TableCell className="text-right">
                      {agency.phone_numbers?.join(', ') || 'غير محدد'}
                    </TableCell>
                    <TableCell className="text-right">{agency.commercial_register_number || 'غير محدد'}</TableCell>
                    <TableCell className="text-right">{agency.tax_registration_number || 'غير محدد'}</TableCell>
                    <TableCell className="text-right">
                      <div className="text-center">
                        <span className={`font-bold text-lg ${
                          (agencyBalances[agency.id] || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {(agencyBalances[agency.id] || 0).toLocaleString()} دج
                        </span>
                        <div className="text-xs text-gray-500">الرصيد الحالي</div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(agency)}
                          className="text-brand-600 hover:text-brand-700"
                          title="تعديل بيانات الوكالة"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleClearAllTransactions(agency.id, agency.name)}
                          className="text-orange-600 hover:text-orange-700 border-orange-300"
                          title="تمام المخالصة - إعادة جميع الحسابات إلى صفر"
                        >
                          🔄 مخالصة
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(agency.id, agency.name)}
                          className="text-red-600 hover:text-red-700"
                          title="حذف الوكالة"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Agency Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[600px]" dir="rtl">
          <DialogHeader>
            <DialogTitle>
              {editingAgency ? '✏️ تعديل الوكالة' : '➕ إضافة وكالة جديدة'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">اسم الوكالة *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="مثال: وكالة صنهاجة للسياحة"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="city">المدينة *</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="مثال: تلمسان"
                  required
                />
              </div>
            </div>

            <div>
              <Label>أرقام الهاتف</Label>
              {formData.phone_numbers.map((phone, index) => (
                <div key={index} className="flex items-center space-x-2 mt-2">
                  <Input
                    value={phone}
                    onChange={(e) => updatePhoneNumber(index, e.target.value)}
                    placeholder="مثال: 043123456"
                    className="flex-1"
                  />
                  {formData.phone_numbers.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removePhoneNumber(index)}
                      className="text-red-600"
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addPhoneNumber}
                className="mt-2"
              >
                <Plus className="h-3 w-3 ml-1" />
                إضافة رقم هاتف
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fax_number">رقم الفاكس</Label>
                <Input
                  id="fax_number"
                  value={formData.fax_number}
                  onChange={(e) => setFormData({ ...formData, fax_number: e.target.value })}
                  placeholder="مثال: 043123457"
                />
              </div>
              
              <div>
                <Label htmlFor="commercial_register_number">رقم السجل التجاري</Label>
                <Input
                  id="commercial_register_number"
                  value={formData.commercial_register_number}
                  onChange={(e) => setFormData({ ...formData, commercial_register_number: e.target.value })}
                  placeholder="مثال: 123456789"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="tax_registration_number">الرقم الضريبي</Label>
              <Input
                id="tax_registration_number"
                value={formData.tax_registration_number}
                onChange={(e) => setFormData({ ...formData, tax_registration_number: e.target.value })}
                placeholder="مثال: 987654321"
              />
            </div>

            <div>
              <Label htmlFor="address">العنوان</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="مثال: شارع العربي بن مهيدي، تلمسان"
              />
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                إلغاء
              </Button>
              <Button type="submit">
                {editingAgency ? '💾 حفظ التغييرات' : '➕ إضافة الوكالة'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* 🆕 Settlement (مخالصة) confirmation dialog — replaces the
          chained native popups which Chrome was silently aborting. */}
      <Dialog
        open={!!settlementTarget}
        onOpenChange={(open) => { if (!open) closeSettlementDialog(); }}
      >
        <DialogContent className="max-w-lg" data-testid="settlement-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700 text-lg">
              ⚠️ تمام المخالصة — إجراء غير قابل للتراجع
            </DialogTitle>
            <DialogDescription className="text-right pt-3 text-sm leading-relaxed">
              أنت على وشك إجراء <span className="font-bold">مخالصة كاملة</span> لـ
              {' '}<span className="font-bold text-red-700">{settlementTarget?.name}</span>.
              <br /><br />
              هذا الإجراء سيحذف نهائياً من قاعدة البيانات:
              <ul className="list-disc pr-5 mt-2 space-y-1">
                <li>كل العمليات اليومية</li>
                <li>كل التحويلات النقدية</li>
                <li>كل المصاريف</li>
                <li>كل الدفعات وخطط التقسيط</li>
                <li>كل مبيعات الخدمات</li>
              </ul>
              <br />
              يُحفظ سجل تدقيق فقط للمراجعة (audit trail).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 pt-2">
            <Label htmlFor="settlement-confirm-input" className="text-sm">
              للمتابعة، اكتب كلمة <span className="font-bold">تأكيد</span> في الحقل أدناه:
            </Label>
            <Input
              id="settlement-confirm-input"
              data-testid="settlement-confirm-input"
              autoFocus
              dir="rtl"
              value={settlementConfirmText}
              onChange={(e) => setSettlementConfirmText(e.target.value)}
              placeholder="اكتب: تأكيد"
              disabled={settlementSubmitting}
            />
          </div>

          <DialogFooter className="gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={closeSettlementDialog}
              disabled={settlementSubmitting}
              data-testid="settlement-cancel-btn"
            >
              إلغاء
            </Button>
            <Button
              type="button"
              onClick={confirmSettlement}
              disabled={settlementSubmitting || settlementConfirmText.trim() !== 'تأكيد'}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="settlement-confirm-btn"
            >
              {settlementSubmitting ? '⏳ جاري التنفيذ...' : '⛔ نعم، نفّذ المخالصة'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default AgencyManagement;
